import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import logo from "./images/logo.png";
import { auth, db } from "./firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  addDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import {
  Home,
  Topics,
  Lessons,
  Videos,
  TopicLessons,
  LessonOverview,
  LessonSteps,
  VideoOverview,
  Artifacts,
} from "./user";
import {
  Users,
  Organizations,
  OrganizationEntry,
  Lessons as AdminLessons,
  LessonEntry,
  Videos as AdminVideos,
  VideoEntry,
  Settings,
  Stats,
} from "./super_admin";
import { AdminDashboard } from "./admin";
import AuthenticatedLayout from "./components/AuthenticatedLayout";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import ProtectedSuperAdminRoute from "./components/ProtectedSuperAdminRoute";
import ScrollToTop from "./components/ScrollToTop";
import { US_STATES } from "./utils/states";

function App() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [currentForm, setCurrentForm] = useState("login"); // "create" or "login"

  // Authentication state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState("user");

  // Organization selection state
  const [selectedState, setSelectedState] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [libraryCard, setLibraryCard] = useState("");

  // New state for targeted sign-up
  // eslint-disable-next-line no-unused-vars
  const [targetOrgId, setTargetOrgId] = useState("");
  const [targetOrg, setTargetOrg] = useState(null);
  const [isTargetedSignup, setIsTargetedSignup] = useState(false);
  const [isAdminSignup, setIsAdminSignup] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [adminSignupCode, setAdminSignupCode] = useState("");

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  // Helper function to convert Firebase errors to user-friendly messages
  const getFriendlyErrorMessage = (error) => {
    const errorCode = error.code;

    switch (errorCode) {
      case "auth/email-already-in-use":
        return "An account with this email already exists. You can reset your password if you've forgotten it.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/weak-password":
        return "Password should be at least 6 characters long.";
      case "auth/user-not-found":
        return "No account found with this email address.";
      case "auth/wrong-password":
        return "Incorrect password. Please try again.";
      case "auth/too-many-requests":
        return "Too many failed attempts. Please try again later.";
      case "auth/network-request-failed":
        return "Network error. Please check your connection and try again.";
      case "auth/popup-closed-by-user":
      case "auth/cancelled-popup-request":
        return "Sign-in was cancelled.";
      case "auth/account-exists-with-different-credential":
        return "An account already exists with this email. Please sign in with your password instead.";
      default:
        return "An error occurred. Please try again or contact support.";
    }
  };

  // Function to fetch organization details by ID
  const fetchOrganizationById = async (orgId) => {
    try {
      const orgDoc = await getDoc(doc(db, "organizations", orgId));
      if (orgDoc.exists()) {
        return { id: orgDoc.id, ...orgDoc.data() };
      }
      return null;
    } catch (error) {
      console.error("Error fetching organization:", error);
      return null;
    }
  };

  // Function to get URL parameters
  const getUrlParams = () => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      return {
        id: urlParams.get("id"),
        code: urlParams.get("code"),
      };
    }
    return { id: null, code: null };
  };

  // Logout function
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Track user login activity
  const trackUserLogin = async (userId) => {
    try {
      // Get user data to find organization_id
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Create login activity record
        const activityData = {
          created_date: new Date(),
          user_id: doc(db, "users", userId),
          organization_id: userData.organization_id,
          type: "user_login",
        };

        // Add to user_activity collection
        const activityRef = collection(db, "user_activity");
        await addDoc(activityRef, activityData);
      }
    } catch (error) {
      console.error("Error tracking login activity:", error);
      // Don't throw error - login tracking failure shouldn't prevent login
    }
  };

  // Fetch user type from Firestore
  const fetchUserType = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.user_type || "user";
      }
      return "user";
    } catch (error) {
      console.error("Error fetching user type:", error);
      return "user";
    }
  };

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        // Fetch user type when user signs in
        const type = await fetchUserType(user.uid);
        setUserType(type);

        // Track login activity
        await trackUserLogin(user.uid);
      } else {
        setUserType("user");
      }
    });

    return () => unsubscribe();
  }, []);

  // Handle targeted sign-up from URL parameter
  useEffect(() => {
    const handleTargetedSignup = async () => {
      const { id, code } = getUrlParams();
      if (id && !user) {
        setTargetOrgId(id);
        setIsTargetedSignup(true);

        // Check if this is an admin signup (both id and code present)
        const isAdminSignupFlag = !!code;
        setIsAdminSignup(isAdminSignupFlag);
        setAdminSignupCode(code || "");

        // Fetch organization details
        const org = await fetchOrganizationById(id);
        if (org && org.state) {
          // For admin signups, validate the admin signup code
          if (isAdminSignupFlag && org.admin_signup_code !== code) {
            setError(
              "Invalid admin signup code. Please contact your organization administrator for the correct code.",
            );
            setIsTargetedSignup(false);
            setTargetOrgId("");
            setTargetOrg(null);
            setIsAdminSignup(false);
            setAdminSignupCode("");
            return;
          }

          setTargetOrg(org);
          setSelectedState(org.state);
          setSelectedOrgId(org.id);
          setOrganizations([org]);

          // Set current form to create account
          setCurrentForm("create");
        } else {
          // Invalid organization ID or missing state
          setError(
            "Invalid organization link or missing organization details. Please contact support.",
          );
          setIsTargetedSignup(false);
          setTargetOrgId("");
          setTargetOrg(null);
          setIsAdminSignup(false);
          setAdminSignupCode("");
        }
      }
    };

    handleTargetedSignup();
  }, [user]);

  // Cleanup targeted sign-up state when user changes
  useEffect(() => {
    if (user) {
      resetTargetedSignup();
    }
  }, [user]);

  // Function to reset targeted sign-up state
  const resetTargetedSignup = () => {
    setTargetOrgId("");
    setTargetOrg(null);
    setIsTargetedSignup(false);
    setIsAdminSignup(false);
    setAdminSignupCode("");
  };

  // Authentication functions
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      // Login successful - user will be redirected to home via useEffect

      clearFormData();
      resetTargetedSignup(); // Reset targeted sign-up state after successful login
    } catch (error) {
      setError(getFriendlyErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const firebaseUser = userCredential.user;

      const userDocRef = doc(db, "users", firebaseUser.uid);
      const existingDoc = await getDoc(userDocRef);

      if (!existingDoc.exists()) {
        // New user - sign them out and redirect to Create Account to collect org info
        await signOut(auth);
        setCurrentForm("create");
        setError(
          "No account found. Please create an account below and choose Sign in with Google.",
        );
      } else {
        clearFormData();
        resetTargetedSignup();
      }
    } catch (error) {
      setError(getFriendlyErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError("");

    if (!selectedState) {
      setError("Please select your state");
      return;
    }

    if (!selectedOrgId) {
      setError("Please select your location");
      return;
    }

    const selectedOrg = organizations.find((o) => o.id === selectedOrgId);

    // Only check for library card if organization has authentication code
    const authCode = selectedOrg?.authentication_code?.toString().trim();
    if (authCode) {
      if (!libraryCard.trim()) {
        const label = selectedOrg?.authentication_label || "Library Card";
        setError(`Please enter your ${label}`);
        return;
      }

      const prefixes = authCode
        .split(",")
        .map((prefix) => prefix.trim())
        .filter(Boolean);
      const cardNumber = libraryCard.trim();
      const isValid = prefixes.some((prefix) => cardNumber.startsWith(prefix));

      if (!isValid) {
        setError(
          "Your card number is not valid. Email hello@fierolearning.com for assistance.",
        );
        return;
      }
    }

    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const firebaseUser = userCredential.user;

      const userDocRef = doc(db, "users", firebaseUser.uid);
      const existingDoc = await getDoc(userDocRef);

      if (!existingDoc.exists()) {
        const userData = {
          created_time: new Date().toISOString(),
          email: firebaseUser.email,
          first_name: firebaseUser.displayName?.split(" ")[0] || "",
          last_name:
            firebaseUser.displayName?.split(" ").slice(1).join(" ") || "",
          organization_id: doc(db, "organizations", selectedOrgId),
          organization_name: selectedOrg?.name || "",
          phone_number: "",
          uid: firebaseUser.uid,
          user_type: "user",
        };
        await setDoc(userDocRef, userData);
      }

      clearFormData();
      resetTargetedSignup();
    } catch (error) {
      setError(getFriendlyErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (!selectedState) {
      setError("Please select your state");
      return;
    }

    if (!selectedOrgId) {
      setError("Please select your location");
      return;
    }

    const selectedOrg = organizations.find((o) => o.id === selectedOrgId);

    // Only check for library card if organization has authentication code
    const authCode = selectedOrg?.authentication_code?.toString().trim();
    if (authCode) {
      if (!libraryCard.trim()) {
        const label = selectedOrg?.authentication_label || "Library Card";
        setError(`Please enter your ${label}`);
        return;
      }

      // Validate library card against authentication_code prefixes
      // Authentication code may be comma-delimited (e.g., "123,456,789")
      const prefixes = authCode
        .split(",")
        .map((prefix) => prefix.trim())
        .filter(Boolean);
      const cardNumber = libraryCard.trim();

      // Check if card number starts with any of the prefixes
      const isValid = prefixes.some((prefix) => cardNumber.startsWith(prefix));

      if (!isValid) {
        setError(
          "Your card number is not valid. Email hello@fierolearning.com for assistance.",
        );
        return;
      }
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      // Create user document in users collection
      try {
        const userDocRef = doc(db, "users", user.uid);

        const userData = {
          created_time: new Date().toISOString(),
          email: user.email,
          first_name: "",
          last_name: "",
          organization_id: doc(db, "organizations", selectedOrgId),
          organization_name: selectedOrg?.name || "",
          phone_number: "",
          uid: user.uid,
          user_type: "user",
        };

        await setDoc(userDocRef, userData);
      } catch (firestoreError) {
        console.error("Firestore error:", firestoreError);
        console.error("Error code:", firestoreError.code);
        console.error("Error message:", firestoreError.message);
        setError(`Database error: ${firestoreError.message}`);
        return;
      }
      clearFormData();
      resetTargetedSignup(); // Reset targeted sign-up state after successful registration
    } catch (error) {
      console.error("Registration error:", error);
      setError(getFriendlyErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const clearFormData = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setLoginEmail("");
    setLoginPassword("");
    setError("");
    setLoading(false);

    // Only clear organization data if not a targeted sign-up
    if (!isTargetedSignup) {
      setSelectedState("");
      setOrganizations([]);
      setSelectedOrgId("");
      setLibraryCard("");
      setOrgsLoading(false);
    }

    setShowForgotPassword(false);
    setForgotPasswordEmail("");
    setForgotPasswordSuccess(false);
  };

  const handleFormSwitch = (formType) => {
    setCurrentForm(formType);
    // Only clear form data if not a targeted sign-up, or if switching away from create form
    if (!isTargetedSignup || formType !== "create") {
      clearFormData();
    } else {
      // For targeted sign-up, only clear the form fields but keep organization data
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setError("");
      setLoading(false);
      setShowForgotPassword(false);
      setForgotPasswordEmail("");
      setForgotPasswordSuccess(false);
      // Note: admin signup state is preserved for targeted signups
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setForgotPasswordLoading(true);

    try {
      await sendPasswordResetEmail(auth, forgotPasswordEmail);
      setForgotPasswordSuccess(true);
      setForgotPasswordEmail("");
    } catch (error) {
      setError(getFriendlyErrorMessage(error));
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordSuccess(false);
    setForgotPasswordEmail("");
    setError("");
  };

  const renderCreateAccountForm = () => (
    <div className="text-left">
      <h3 className="text-xl sm:text-2xl font-bold text-primary-500 mb-2">
        {isAdminSignup ? "Create Admin Account" : "Create Account"}
      </h3>
      <p className="text-gray-600 mb-2 text-sm sm:text-base">
        <strong>{isTargetedSignup && targetOrg?.name}</strong>
      </p>
      <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
        {isAdminSignup
          ? "You're creating an admin account for this organization."
          : "Let's get started by filling out the form below."}
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      <form className="space-y-3 sm:space-y-4" onSubmit={handleRegister}>
        {/* Email */}
        <div>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base touch-target"
            required
          />
        </div>

        {/* State - hidden for targeted sign-ups */}
        {!isTargetedSignup && (
          <div>
            <select
              className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white text-base touch-target"
              value={selectedState}
              onChange={async (e) => {
                const value = e.target.value;
                setSelectedState(value);
                // Reset downstream selections
                setSelectedOrgId("");
                setLibraryCard("");
                setOrganizations([]);
                if (!value) return;

                setOrgsLoading(true);
                try {
                  const orgsRef = collection(db, "organizations");
                  const q = query(
                    orgsRef,
                    where("state", "==", value),
                    orderBy("name"),
                  );
                  const snap = await getDocs(q);
                  const list = snap.docs.map((d) => ({
                    id: d.id,
                    ...d.data(),
                  }));
                  setOrganizations(list);
                } catch (fetchError) {
                  console.error("Error fetching organizations:", fetchError);
                  setOrganizations([]);
                } finally {
                  setOrgsLoading(false);
                }
              }}
              required
            >
              <option value="">State</option>
              {US_STATES.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.code} - {state.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Location - hidden for targeted sign-ups */}
        {!isTargetedSignup &&
          selectedState &&
          !orgsLoading &&
          organizations.length > 0 && (
            <div>
              <select
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white text-base touch-target"
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                required
              >
                <option value="">Location</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}

        {/* No locations message - hidden for targeted sign-ups */}
        {!isTargetedSignup &&
          selectedState &&
          !orgsLoading &&
          organizations.length === 0 && (
            <div className="text-gray-600 text-sm">
              No locations in this state
            </div>
          )}

        {/* Library Card Number - shown after location is chosen and only if org has auth code */}
        {selectedOrgId &&
          organizations
            .find((o) => o.id === selectedOrgId)
            ?.authentication_code?.trim() && (
            <div>
              <input
                type="text"
                value={libraryCard}
                onChange={(e) => setLibraryCard(e.target.value)}
                placeholder={
                  (organizations.find((o) => o.id === selectedOrgId)
                    ?.authentication_label || "Library Card") + " Number"
                }
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base touch-target"
                required
              />
            </div>
          )}

        {/* Passwords - shown after location is chosen */}
        {selectedOrgId && (
          <>
            {/* Password */}
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-12 text-base touch-target"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-target"
              >
                {showPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-12 text-base touch-target"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-target"
              >
                {showConfirmPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </>
        )}

        {/* Create Account with Google - uses Google for auth, form data for org/location */}
        <button
          type="button"
          onClick={handleGoogleSignUp}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed touch-target text-base"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {loading ? "Creating account..." : "Create Account with Google"}
        </button>

        {/* Create Account with Email - uses email/password, no Google */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary-500 text-white py-3 px-4 sm:px-6 rounded-lg hover:bg-primary-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed touch-target text-base"
        >
          {loading
            ? isAdminSignup
              ? "Creating Admin Account..."
              : "Creating account..."
            : isAdminSignup
              ? "Create Admin Account"
              : "Create Account with Email"}
        </button>
      </form>

      {/* Back to Login Button */}
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={() => handleFormSwitch("login")}
          className="text-primary-600 hover:text-primary-800 transition-colors text-sm sm:text-base touch-target"
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );

  const renderLoginForm = () => (
    <div className="text-left">
      <h3 className="text-xl sm:text-2xl font-bold text-primary-500 mb-2">
        Welcome Back!
      </h3>
      <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
        Fill out the information below to access your account.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      <form className="space-y-3 sm:space-y-4" onSubmit={handleLogin}>
        {/* Email */}
        <div>
          <input
            type="email"
            placeholder="Email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base touch-target"
            required
          />
        </div>

        {/* Password */}
        <div className="relative mb-1">
          <input
            type={showLoginPassword ? "text" : "password"}
            placeholder="Password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-12 text-base touch-target"
            required
          />
          <button
            type="button"
            onClick={() => setShowLoginPassword(!showLoginPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-target"
          >
            {showLoginPassword ? (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Forgot Password Link */}
        <div className="text-left" style={{ marginTop: 0 }}>
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="text-gray-700 hover:text-primary-700 transition-colors text-sm sm:text-base touch-target"
          >
            Forgot Password?
          </button>
        </div>

        {/* Sign In with Email Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary-500 text-white py-3 px-4 sm:px-6 rounded-lg hover:bg-primary-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed touch-target text-base"
        >
          {loading ? "Signing in..." : "Sign In with Email"}
        </button>

        {/* Continue with Google - below Sign In */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed touch-target text-base"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {loading ? "Signing in..." : "Sign in with Google"}
        </button>

        {/* Sign Up Link */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => handleFormSwitch("create")}
            className="text-gray-700 text-xl sm:text-lg hover:text-primary-700 transition-colors touch-target"
          >
            Don't have an account?{" "}
            <span className="text-primary-700 font-bold">Sign Up</span>
          </button>
        </div>
      </form>
    </div>
  );

  const renderForgotPasswordForm = () => (
    <div className="text-left">
      <h3 className="text-xl sm:text-2xl font-bold text-primary-500 mb-2">
        Reset Password
      </h3>
      <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
        Enter your email address and we'll send you a link to reset your
        password.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      {forgotPasswordSuccess ? (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm">
          <p className="mb-2">Password reset email sent!</p>
          <p className="text-sm">
            Check your email for a link to reset your password.
          </p>
        </div>
      ) : (
        <form
          className="space-y-3 sm:space-y-4"
          onSubmit={handleForgotPassword}
        >
          {/* Email */}
          <div>
            <input
              type="email"
              placeholder="Email"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base touch-target"
              required
            />
          </div>

          {/* Send Reset Button */}
          <button
            type="submit"
            disabled={forgotPasswordLoading}
            className="w-full bg-primary-500 text-white py-3 px-4 sm:px-6 rounded-lg hover:bg-primary-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed touch-target text-base"
          >
            {forgotPasswordLoading ? "Sending..." : "Send Reset Link"}
          </button>
        </form>
      )}

      {/* Back to Login Button */}
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={handleBackToLogin}
          className="text-primary-600 hover:text-primary-800 transition-colors text-sm sm:text-base touch-target"
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#10B981",
              secondary: "#fff",
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />
      {user ? (
        <Router>
          <ScrollToTop />
          <AuthenticatedLayout
            onLogout={handleLogout}
            userType={userType}
            userEmail={user.email}
          >
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/topics" element={<Topics />} />
              <Route
                path="/topics/:topic"
                element={<TopicLessons userType={userType} />}
              />
              <Route
                path="/lessons"
                element={<Lessons userType={userType} />}
              />
              <Route path="/videos" element={<Videos />} />
              <Route path="/gallery" element={<Artifacts />} />
              <Route
                path="/admin/lessons"
                element={
                  <ProtectedSuperAdminRoute userType={userType}>
                    <AdminLessons />
                  </ProtectedSuperAdminRoute>
                }
              />
              <Route
                path="/admin/lesson/:lessonId"
                element={
                  <ProtectedSuperAdminRoute userType={userType}>
                    <LessonEntry />
                  </ProtectedSuperAdminRoute>
                }
              />
              <Route
                path="/admin/videos"
                element={
                  <ProtectedAdminRoute userType={userType}>
                    <AdminVideos />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/video/:videoId"
                element={
                  <ProtectedAdminRoute userType={userType}>
                    <VideoEntry />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/organizations"
                element={
                  <ProtectedAdminRoute userType={userType}>
                    <Organizations />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/organizations/new"
                element={
                  <ProtectedAdminRoute userType={userType}>
                    <OrganizationEntry />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/organizations/:orgId"
                element={
                  <ProtectedAdminRoute userType={userType}>
                    <OrganizationEntry />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedAdminRoute userType={userType}>
                    <Users />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <ProtectedAdminRoute userType={userType}>
                    <Settings />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin/stats"
                element={
                  <ProtectedAdminRoute userType={userType}>
                    <Stats />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/admin-dashboard"
                element={
                  <ProtectedAdminRoute userType={userType}>
                    <AdminDashboard user={user} userType={userType} />
                  </ProtectedAdminRoute>
                }
              />
              <Route
                path="/lesson/:lessonId"
                element={<LessonOverview userType={userType} />}
              />
              <Route
                path="/lesson-steps/:lessonId"
                element={<LessonSteps userType={userType} />}
              />
              <Route path="/video/:videoId" element={<VideoOverview />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthenticatedLayout>
        </Router>
      ) : (
        <div className="flex flex-col items-center justify-center py-4 px-4 sm:py-8 sm:px-6">
          <div className="w-full max-w-md">
            {/* Logo and Branding */}
            <div className="text-center mb-12 sm:mb-12">
              <img
                src={logo}
                alt="KUZKO HISTORY"
                className="h-12 w-auto sm:h-16 mx-auto mb-3 sm:mb-4"
              />
            </div>

            {/* Form Container */}
            {currentForm === "create"
              ? renderCreateAccountForm()
              : showForgotPassword
                ? renderForgotPasswordForm()
                : renderLoginForm()}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
